import { Node, mergeAttributes } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    mailSignature: {
      setMailSignature: (attrs: { id: string; html: string }) => ReturnType;
      removeMailSignature: () => ReturnType;
    };
  }
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function findSignatureRange(doc: ProseMirrorNode): { from: number; to: number } | null {
  let range: { from: number; to: number } | null = null;
  doc.descendants((node, pos) => {
    if (range) return false;
    if (node.type.name !== 'mailSignature') return;
    range = { from: pos, to: pos + node.nodeSize };
    return false;
  });
  return range;
}

export const MailSignature = Node.create({
  name: 'mailSignature',
  group: 'block',
  content: 'block+',
  defining: true,
  isolating: true,

  addAttributes() {
    return {
      signatureId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-mail-signature'),
        renderHTML: (attributes) =>
          attributes.signatureId
            ? { 'data-mail-signature': attributes.signatureId }
            : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-mail-signature]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'manta_signature' }),
      0,
    ];
  },

  addCommands() {
    return {
      setMailSignature:
        ({ id, html }) =>
        ({ chain, state }) => {
          const wrapped = `<div data-mail-signature="${escapeAttr(id)}" class="manta_signature">${html}</div>`;
          const range = findSignatureRange(state.doc);
          if (range) {
            return chain()
              .insertContentAt(range, wrapped, { updateSelection: false })
              .run();
          }
          return chain()
            .insertContentAt(state.doc.content.size, `<p></p>${wrapped}`, {
              updateSelection: false,
            })
            .run();
        },
      removeMailSignature:
        () =>
        ({ tr, state, dispatch }) => {
          const range = findSignatureRange(state.doc);
          if (range && dispatch) tr.delete(range.from, range.to);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const type = this.type;
    return [
      new Plugin({
        key: new PluginKey('mailSignatureBodyLine'),
        appendTransaction: (_transactions, _oldState, state) => {
          const paragraph = state.schema.nodes.paragraph;
          if (!paragraph || state.doc.firstChild?.type !== type) return null;
          const tr = state.tr.insert(0, paragraph.create());
          if (state.selection.from <= 2) {
            tr.setSelection(TextSelection.create(tr.doc, 1));
          }
          return tr;
        },
      }),
    ];
  },
});
